import { FC, useEffect, useMemo, useState, useEffectEvent } from 'react'
import { formatRFC3339DateTimeOrEmpty } from '~/shared/lib/date'
import { Box, Chip, Drawer, Fade, Grid } from '@mui/material'
import { ProvidersWorkUnitsWorklistsListQueryParams } from '@fireflyhealth/api'
import { useUpdateWorkUnitItem, useWorkListsById, useWorkUnitItems } from './WorkListsService'

import {
  DataGrid,
  GridColDef,
  GridColType,
  GridCellParams,
  GridRenderCellParams,
  GridLogicOperator,
  GridColumnVisibilityModel,
  useGridApiRef,
  GridRowModel,
  GridRowsProp,
  GridFilterOperator,
  GridSortModel,
} from '@mui/x-data-grid'
import WorklistLinks from './WorklistLinks'
import { Loader } from '~/shared/ui'
import { AssigneeGroupListItem, UserGroupAvatar } from '~/entities/assignee-group'
import EditableUserDataCell from './EditableUserDataCell'
import { usePersonCaseCategoriesById } from '~/entities/case'
import { getUserById } from '~/shared/lib/names'
import { worklistPreferencesSlice, FilterItem, WorklistFilterPreference } from './preferencesSlice'
import { WorklistGridToolbar } from './WorklistGridToolbar'
import { PHIAccessBlock } from '~/shared/security'
import { PatientDetailContextProvider } from '~/features/member-profile'
import WorklistEditor from './WorklistEditor'
import {
  worklistCareTeamFilterOperators,
  worklistNextActionDateFilterOperators,
  WorklistRowBuilder,
} from './utils'
import { useTypedWorklistSelector, useWorklistDispatch } from './store'
import { Provider } from '~/entities/provider'
import { CaseTag, type CaseAction, type Tag } from '~/entities/work-unit'
import { WorklistFilterUrlParam } from './Worklist'
import { WorklistsListResponse } from '@fireflyhealth/api'
import { fireAndForget } from '~/shared/lib/fireAndForget'
import { asDisplayText } from '~/shared/lib/displayText'
import { haveChanged } from '~/shared/lib/haveChanged'
import { required } from '~/shared/lib/required'

const drawerWidth = '40%'
const fieldType = 'item'

interface WorkunitTableSort {
  field: string
  sort: string
  type: string
}

/**
 * Translates the filter model into the shape the worklist endpoint reads.
 *
 * The panel and the persisted preference name the operator `linkOperator`, following MUI's
 * `GridLogicOperator`, but the API reads `link_operator` and only treats the exact value `or`
 * as a disjunction — anything else combines the items with AND. Renaming on the way out keeps
 * the filter models already in session storage and in shared worklist URLs readable.
 */
const toApiFilterModel = (filters: WorklistFilterPreference) => ({
  link_operator: filters.linkOperator?.toLowerCase(),
  items: filters.items,
})

const TagDataCell = (params: GridCellParams<WorkUnitGridRow, Tag[]>) =>
  params.value?.map(caseTag => {
    return <CaseTag tagKey={String(caseTag.id)} tag={caseTag} key={String(caseTag.id)} />
  })

interface WorklistProps {
  worklistId?: number
  providers?: Provider[]
  assigneeGroups?: AssigneeGroupListItem[]
  setWorklistFiltersUrlParam: (param: WorklistFilterUrlParam | undefined) => void
  worklistFiltersUrlParam?: WorklistFilterUrlParam
}

export interface WorkUnitRowData {
  ownerGroup?: string
  data?: WorkUnitRowData
  links?: { caseUrl: string }
  elationUrl?: string | null
  actions?: CaseAction[]
  patientId?: string
  pk: string
  status?: string
  worklist: WorklistsListResponse[number]
  description: string | null
  notes: string | null
  createdAt: string | null
  person: string | null
  careTeam: string | null
  podNames?: string | null
  dueDate: string | null
  contentType: 'case' | null
  category: string | null
  tags?: Tag[]
  statusCategory?: string
}

export interface WorkUnitGridRow extends WorkUnitRowData, GridRowModel {}

export type WorklistGridRow = WorkUnitGridRow

/**
 * Commits an edited cell value. The popper edits every field through one text input, so
 * the value arrives unnarrowed and each handler narrows to what its own field accepts.
 */
export type WorkUnitCellEditHandler = (
  workUnit: WorkUnitGridRow,
  field: string,
  value: unknown,
) => void

const workUnitFields: {
  prop: keyof WorkUnitRowData
  headerName: string
  dataType: GridColType
  width: number
  filterable: boolean
  sortable: boolean
  filterOperators?: GridFilterOperator[]
  renderCell?: (params: GridRenderCellParams) => React.ReactNode
}[] = [
  {
    prop: 'tags',
    headerName: 'Tags',
    dataType: 'string',
    width: 200,
    filterable: true,
    sortable: false,
    renderCell: TagDataCell,
  },
  {
    prop: 'person',
    headerName: 'Person',
    dataType: 'string',
    width: 200,
    filterable: true,
    sortable: true,
  },
  {
    prop: 'category',
    headerName: 'Case Category',
    dataType: 'string',
    width: 200,
    filterable: true,
    sortable: true,
  },
  {
    prop: 'description',
    headerName: 'Description',
    dataType: 'string',
    width: 200,
    filterable: true,
    sortable: true,
  },
  {
    prop: 'notes',
    headerName: 'Notes',
    dataType: 'string',
    width: 200,
    filterable: true,
    sortable: true,
  },
  {
    prop: 'createdAt',
    headerName: 'Case Created',
    dataType: 'date',
    width: 200,
    filterable: true,
    sortable: true,
  },
  {
    prop: 'careTeam',
    headerName: 'Care Team',
    dataType: 'string',
    width: 200,
    filterable: true,
    filterOperators: worklistCareTeamFilterOperators,
    sortable: false,
  },
  {
    prop: 'podNames',
    headerName: 'Care Pod',
    dataType: 'string',
    width: 200,
    filterable: true,
    sortable: false,
  },
  {
    prop: 'statusCategory',
    headerName: 'Status Category',
    dataType: 'string',
    width: 200,
    filterable: true,
    sortable: false,
  },
]

const ROWS_PER_PAGE = 50

const WorkUnitTable: FC<WorklistProps> = (props: WorklistProps) => {
  const [itemCount, setItemCount] = useState<number>(0)
  const [rows, setRows] = useState<GridRowsProp>([])
  const [columns, setColumns] = useState<GridColDef[]>([])
  const [filters, setFilters] = useState<WorklistFilterPreference | undefined>(undefined)
  const [worklistSort, setWorklistSort] = useState<WorkunitTableSort | undefined>(undefined)
  const [worklistDataloading, setWorklistDataloading] = useState<boolean>(false)
  const [page, setPage] = useState<number>(0)
  const [modalData, setModalData] = useState<WorklistGridRow | null>(null)
  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const [worklistMetaDataloading, setWorklistMetaDataloading] = useState<boolean>(false)
  const [title, setWorklistTitle] = useState<string>('')
  const apiRef = useGridApiRef()
  const worklistDispatch = useWorklistDispatch()
  const { result: workListsById, isLoading: isWorklistLoading } = useWorkListsById()
  const options = useMemo<ProvidersWorkUnitsWorklistsListQueryParams>(
    () => ({
      limit: ROWS_PER_PAGE,
      offset: page * ROWS_PER_PAGE,
      // The endpoint takes both models as JSON-encoded strings.
      filters: filters && JSON.stringify(toApiFilterModel(filters)),
      sort: worklistSort && JSON.stringify(worklistSort),
    }),
    [page, filters, worklistSort],
  )
  const { result: workUnits, isLoading: isWorkUnitLoading } = useWorkUnitItems(props.worklistId, {
    ...options,
  })
  // Use full, unfiltered catalog for robust title lookup across patients
  const { result: caseCategoriesById, isLoading: isCaseCategoryLoading } =
    usePersonCaseCategoriesById(null)
  const { isPending: isUpdatingWorkUnit, mutateAsync: handleUpdateWorkUnit } =
    useUpdateWorkUnitItem()
  const worklistPreferences = useTypedWorklistSelector(state =>
    props.worklistId ? state.worklistPreferences.byId[props.worklistId] : {},
  )
  const isLoading =
    isWorklistLoading || isWorkUnitLoading || isCaseCategoryLoading || isUpdatingWorkUnit

  const workList = useMemo(
    () => (props.worklistId ? workListsById[props.worklistId] : undefined),
    [props.worklistId, workListsById],
  )

  // Column visibility is a controlled model in data-grid v6 (the per-column `hide`
  // prop was removed); derive it from the persisted per-field preferences.
  const columnVisibilityModel = useMemo<GridColumnVisibilityModel>(() => {
    const visibility = worklistPreferences?.visibility ?? {}
    const model: GridColumnVisibilityModel = {}
    for (const [field, preference] of Object.entries(visibility)) {
      model[field] = preference.isVisible
    }
    return model
  }, [worklistPreferences])

  // A different worklist starts on its first page.
  const [pagedWorklistId, setPagedWorklistId] = useState(props.worklistId)
  if (props.worklistId !== pagedWorklistId) {
    setPagedWorklistId(props.worklistId)
    setPage(0)
  }

  const handleDueDateEdit: WorkUnitCellEditHandler = (workUnit, _field, value) => {
    // The popper edits through a TextField, so the value is a string; anything else
    // is rejected rather than parsed, so it cannot silently overwrite the due date.
    if (typeof value !== 'string') {
      return
    }
    fireAndForget('[Worklist] handleDueDateEdit', () =>
      handleUpdateWorkUnit({
        object_id: Number(workUnit.id),
        content_type: required(workUnit.contentType, 'a content type on the work unit'),
        due_date: formatRFC3339DateTimeOrEmpty(value),
      }),
    )
  }

  // The column set for the current worklist, built from the props of the moment.
  const createColumns = (): GridColDef[] => {
    const columns: GridColDef[] = []

    columns.push({
      field: 'data',
      headerName: 'Links',
      sortable: false,
      filterable: false,
      width: 200,
      renderCell: (params: GridRenderCellParams<WorkUnitGridRow, WorkUnitGridRow>) => (
        <WorklistLinks
          showEditLink={true}
          onEditTask={() => {
            setModalData(params.value ?? null)
            setModalOpen(true)
          }}
          {...params}
        />
      ),
    })
    columns.push({
      field: 'status',
      headerName: 'Status',
      sortable: true,
      width: 150,
      renderCell: (params: GridCellParams) => {
        const status = asDisplayText(params.value)
        return status ? (
          <Chip label={status.toUpperCase()} />
        ) : (
          <Box sx={{ display: 'flex', margin: '1rem', padding: '1rem' }}>-</Box>
        )
      },
    })
    columns.push({
      field: 'assignedToName',
      headerName: 'Owner',
      sortable: false,
      width: 100,
      renderCell: (params: GridCellParams<WorkUnitGridRow>) =>
        params.value ? (
          <UserGroupAvatar
            id={Number(params.row.ownerGroup)}
            size="small"
            avatarStyle={{ margin: 0, display: 'inline-flex' }}
          />
        ) : (
          <Box sx={{ display: 'flex', margin: '1rem', padding: '1rem' }}>-</Box>
        ),
    })
    columns.push({
      type: 'date',
      field: 'dueDate',
      headerName: 'Next Action Date',
      sortable: true,
      filterable: true,
      filterOperators: worklistNextActionDateFilterOperators,
      width: 182,
      // The cell value is a preformatted date string, not a Date object, so the
      // default `date` valueFormatter (which requires a Date) would throw. Pass
      // it through; the custom renderCell below owns the actual display.
      valueFormatter: (value: string | null) => value ?? '-',
      renderCell: (params: GridCellParams) => (
        <EditableUserDataCell
          editable={true}
          field="dueDate"
          handleOnEdit={handleDueDateEdit}
          value={params.value}
          worklistRowData={required(
            (params.row as WorkUnitGridRow).data,
            'row data from WorklistRowBuilder',
          )}
        />
      ),
    })
    for (const workUnitDatum of workUnitFields) {
      const {
        prop,
        headerName,
        dataType,
        width,
        filterable,
        filterOperators,
        sortable,
        renderCell,
      } = workUnitDatum
      let currentColumnDefinition: GridColDef = {
        type: dataType,
        field: prop,
        headerName: headerName,
        sortable: sortable,
        filterable: filterable,
        width: width,
        valueFormatter: (value: unknown) => value ?? '-',
        renderCell: renderCell ?? ReadOnlyLookerDataCell,
      }
      if (filterOperators) {
        currentColumnDefinition = {
          ...currentColumnDefinition,
          filterOperators: filterOperators,
        }
      }
      columns.push(currentColumnDefinition)
    }
    return columns
  }

  // The title and columns are rebuilt whenever the worklist changes, and loading until it has.
  const metaDataSource = [workList, props.worklistId]
  const [metaDataFrom, setMetaDataFrom] = useState<unknown[]>()
  if (haveChanged(metaDataFrom, metaDataSource)) {
    setMetaDataFrom(metaDataSource)
    setWorklistMetaDataloading(!workList)
    if (workList) {
      setWorklistTitle(workList.name ?? `Untitled Worklist (ID ${workList.id})`)
      setColumns(createColumns())
    }
  }

  // Seeds the grid's filter state once the worklist has loaded. Editing a filter writes back to
  // the saved preference and the URL param, so both are read through the event rather than
  // depended on, which would re-seed the grid from its own edits.
  const seedGridFilters = useEffectEvent(() => {
    if (apiRef.current != null && !worklistDataloading && !isLoading && workList) {
      apiRef.current.setSortModel([])
      const worklistFilters =
        props.worklistFiltersUrlParam?.worklistId === props.worklistId &&
        props.worklistFiltersUrlParam?.filters
          ? props.worklistFiltersUrlParam?.filters
          : worklistPreferences?.filter
      // The community grid has no built-in filter panel; the custom panel owns
      // filtering, so seed the filter state (server query + panel) directly from
      // saved preferences.
      setFilters(worklistFilters ?? undefined)
    }
  })
  useEffect(() => {
    seedGridFilters()
  }, [props.worklistId, worklistDataloading, isLoading, workList])

  // The rows are rebuilt from the loaded work units whenever any of their inputs change.
  const rowsSource = [
    workList,
    workUnits,
    isLoading,
    props.providers,
    worklistMetaDataloading,
    props.assigneeGroups,
    caseCategoriesById,
  ]
  const [rowsFrom, setRowsFrom] = useState<unknown[]>()
  if (haveChanged(rowsFrom, rowsSource)) {
    setRowsFrom(rowsSource)
    // start filling up the rows only when worklist metadata has completed loading
    const worklistLoaded =
      workList &&
      workUnits &&
      !isLoading &&
      !worklistMetaDataloading &&
      props.providers &&
      props.providers?.length > 0

    if (worklistLoaded) {
      setItemCount(workUnits.count)
      setRows(
        new WorklistRowBuilder({
          worklist: workList,
          items: workUnits.items,
          providers: props.providers,
          assigneeGroups: props.assigneeGroups,
          getUserById,
          caseCategoriesById: caseCategoriesById,
        }).createRows(),
      )
    }
    setWorklistDataloading(false)
  }

  const hideWorklistDetails = () => {
    setModalOpen(false)
    setModalData(null)
  }

  const handleColumnVisibilityModelChange = (newModel: GridColumnVisibilityModel) => {
    if (!props.worklistId) {
      return
    }
    // v6 hands us the full visibility model; persist only the fields that changed.
    const fields = new Set([...Object.keys(columnVisibilityModel), ...Object.keys(newModel)])
    for (const field of fields) {
      const wasVisible = columnVisibilityModel[field] !== false
      const isVisible = newModel[field] !== false
      if (wasVisible !== isVisible) {
        worklistDispatch(
          worklistPreferencesSlice.actions.setVisibility({
            worklistId: props.worklistId,
            field,
            isVisible,
          }),
        )
      }
    }
  }

  // Grid-agnostic filter application: persists to Redux + the URL and drives the
  // server query. The community custom filter panel feeds its {linkOperator, items}
  // model through here.
  const applyFilterModel = (model: WorklistFilterPreference) => {
    if (!props.worklistId) {
      return
    }
    const items: FilterItem[] = model.items
      .filter(item => !!item.field && !!item.operator)
      .map(item => ({ field: item.field, operator: item.operator, value: item.value ?? '' }))
    const selectedFilters: WorklistFilterPreference = {
      linkOperator: model.linkOperator || GridLogicOperator.Or,
      items,
    }
    worklistDispatch(
      worklistPreferencesSlice.actions.setFilter({
        worklistId: props.worklistId,
        filter: selectedFilters,
      }),
    )
    props.setWorklistFiltersUrlParam(
      items.length > 0 ? { worklistId: props.worklistId, filters: selectedFilters } : undefined,
    )
    setFilters(selectedFilters)
  }

  const handleColumnSort = (model: GridSortModel) => {
    if (props.worklistId) {
      let worklistSort: WorkunitTableSort | undefined = undefined
      if (model.length > 0) {
        const sortModel = model[0]
        worklistSort = { ...sortModel, type: fieldType } as WorkunitTableSort
        setWorklistSort(worklistSort)
      }
    }
  }

  const currentFilterModel: WorklistFilterPreference =
    filters && Array.isArray(filters.items)
      ? filters
      : { linkOperator: GridLogicOperator.And, items: [] }

  if (worklistDataloading) {
    return <Loader />
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box component="main" sx={{ flexGrow: 1 }}>
        <Box sx={{ fontSize: '3rem', padding: '1.5em 0em 0.5em 1em' }}>
          <Grid container direction="row" sx={{ alignItems: 'flex-end' }}>
            <Grid size={8}>{title}</Grid>
          </Grid>
        </Box>
        <Box sx={{ flex: 1, height: '85%', overflow: 'auto' }}>
          <DataGrid
            rowCount={itemCount}
            pagination
            paginationMode="server"
            paginationModel={{ page, pageSize: ROWS_PER_PAGE }}
            pageSizeOptions={[ROWS_PER_PAGE]}
            onPaginationModelChange={model => setPage(model.page)}
            columns={columns}
            // Community DataGrid always disables multiple row selection (it is a
            // forced prop), so it is not passed here.
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
            // The built-in filter UI (single condition only) is disabled; the
            // custom toolbar panel drives multi-condition filtering via applyFilterModel.
            disableColumnFilter
            filterMode="server"
            onSortModelChange={handleColumnSort}
            sortingMode="server"
            rows={rows}
            loading={isLoading || worklistMetaDataloading}
            // v7 replaced the column-count buffer with a pixel one. The grid's
            // 13 columns average ~187px, so the previous `columnBuffer={10}`
            // kept virtually every column mounted; 2000px preserves that.
            columnBufferPx={2000}
            apiRef={apiRef}
            // v8 hides the toolbar unless asked (`showToolbar` defaults to false), and
            // supplying `slots.toolbar` alone is not enough. The toolbar carries this
            // grid's only filter entry point, since `disableColumnFilter` above turns off
            // the built-in one.
            showToolbar
            slots={{
              toolbar: WorklistGridToolbar,
            }}
            slotProps={{
              toolbar: {
                columns,
                model: currentFilterModel,
                onApply: applyFilterModel,
              },
            }}
          />
        </Box>
      </Box>
      {modalData !== null && modalOpen && (
        <Fade in={true}>
          <div>
            <Drawer
              anchor={'right'}
              sx={{ width: drawerWidth, height: '100%', flexShrink: 0 }}
              variant="persistent"
              slotProps={{ paper: { sx: { width: drawerWidth } } }}
              open={true}
            >
              <PHIAccessBlock
                userId={modalData.patientId == null ? null : Number(modalData.patientId)}
              >
                <PatientDetailContextProvider>
                  <WorklistEditor worklistRow={modalData} handleClose={hideWorklistDetails} />
                </PatientDetailContextProvider>
              </PHIAccessBlock>
            </Drawer>
          </div>
        </Fade>
      )}
    </Box>
  )
}
const ReadOnlyLookerDataCell = (params: GridCellParams) => (
  <div title={params.value?.toString()}>{params.value?.toString()}</div>
)

export default WorkUnitTable
